import { gql } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  ModalContent as ChakraModalContent,
  IconButton,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';
import * as React from 'react';

import { Loading } from '@/imports/ui/chakra/components/Loading';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FolderIcon,
  XIcon,
} from '@/imports/ui/chakra/feather';

import { MoveItemsModal_LibraryFolderFragment } from './graphql-types/MoveItemsModal_LibraryFolderFragment';

export const MOVE_ITEMS_MODAL_LIBRARY_FOLDER_FRAGMENT = gql`
  fragment MoveItemsModal_LibraryFolderFragment on LibraryFolder {
    _id
    name
    parentFolder {
      _id
      name
    }
  }
`;

type Folder = MoveItemsModal_LibraryFolderFragment;

type IFolderNavigationListItemProps = {
  folder: Folder;
  isLast: boolean;
  isSelected: boolean;
  onClickSelect: () => void;
  onClickNavigateToFolder: () => void;
  isDisabled: boolean;
};

const FolderNavigationListItem: React.FC<IFolderNavigationListItemProps> = (
  props
) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      bg={props.isSelected ? 'blue.50' : undefined}
      onClick={props.isDisabled ? undefined : props.onClickSelect}
      opacity={props.isDisabled ? 0.5 : undefined}
      cursor="pointer"
      px="4"
      py="2"
    >
      <FolderIcon />
      <Text noOfLines={1} title={props.folder.name} flex="1">
        {props.folder.name}
      </Text>
      <IconButton
        variant="ghost"
        colorScheme="gray"
        aria-label={`open ${props.folder.name}`}
        onClick={props.isDisabled ? undefined : props.onClickNavigateToFolder}
        icon={<ChevronRightIcon />}
        visibility={props.isDisabled ? 'hidden' : undefined}
      />
    </Stack>
  );
};

type IFolderNavigationListProps = {
  loadingFolders: boolean;
  currentFolder: Folder;
  onClickNavigateToFolder: (folderId: string) => void;
  onSelectHandler: (folderId: string) => void;
  subFolders: Folder[];
  selectedFolderId?: string;
  disableFolderId?: string;
};

const FolderNavigationList: React.FC<IFolderNavigationListProps> = (props) => {
  if (props.loadingFolders) return null;

  return (
    <Stack spacing="0" minH="sm">
      {props.subFolders.map((folder, idx) => {
        return (
          <FolderNavigationListItem
            folder={folder}
            key={folder._id}
            isLast={props.subFolders.length - 1 === idx}
            isSelected={
              !!props.selectedFolderId && props.selectedFolderId === folder._id
            }
            onClickSelect={() => props.onSelectHandler(folder._id)}
            onClickNavigateToFolder={() => {
              props.onClickNavigateToFolder(folder._id);
            }}
            isDisabled={
              !!props.disableFolderId && props.disableFolderId === folder._id
            }
          />
        );
      })}
    </Stack>
  );
};

type IFolderTreeLocationProps = {
  currentFolder?: Folder;
  rootFolderId: string;
  onClickNavigateToFolder: (folderId: string) => void;
  subFolders: Folder[];
};

type IModalContentProps = IFolderTreeLocationProps & {
  loadingFolders: boolean;
  loadingSubmit: boolean;
  onClickCancel: () => void;
  onClickSubmitMove: (destinationFolderId: string) => void;
  disableFolderId?: string;
  open: boolean;
  onClose: () => void;
};

const ModalContent: React.FC<IModalContentProps> = (props) => {
  const {
    currentFolder,
    subFolders,
    rootFolderId,
    onClickNavigateToFolder,
    loadingFolders,
    loadingSubmit,
    onClickCancel,
    onClickSubmitMove,
    onClose,
  } = props;

  const canShowBack = !!currentFolder && currentFolder._id !== rootFolderId;
  const canShowFolders = !!currentFolder && !loadingFolders;

  const [selectedFolderId, setSelectedFolderId] = React.useState<
    string | undefined
  >(undefined);

  const onSelectHandler = (clickedFolderId: string): void => {
    setSelectedFolderId((selectedFolderId) => {
      return selectedFolderId === clickedFolderId ? undefined : clickedFolderId;
    });
  };

  const onClickNavigateToFolderHandler = (folderId: string): void => {
    setSelectedFolderId(undefined);
    onClickNavigateToFolder(folderId);
  };

  const destinationFolderId = selectedFolderId || currentFolder?._id;
  const submitButtonText =
    !currentFolder ||
    !destinationFolderId ||
    destinationFolderId === currentFolder._id
      ? 'Move Here'
      : 'Move';

  const headerTitleText =
    currentFolder && currentFolder._id !== rootFolderId
      ? currentFolder.name
      : 'Move';
  const header = (
    <Stack direction="row" alignItems="center" spacing="4">
      {loadingFolders ? (
        <Loading />
      ) : currentFolder ? (
        <>
          {canShowBack && (
            <IconButton
              aria-label="back"
              icon={<ChevronLeftIcon />}
              variant="outline"
              colorScheme="gray"
              onClick={() => {
                currentFolder.parentFolder &&
                  onClickNavigateToFolderHandler(
                    currentFolder.parentFolder._id
                  );
              }}
            />
          )}
          <Text noOfLines={1}>{headerTitleText}</Text>
        </>
      ) : null}
      <Spacer />
      <IconButton
        aria-label="close"
        icon={<XIcon />}
        variant="ghost"
        colorScheme="gray"
        onClick={onClose}
      />
    </Stack>
  );

  const footer = (
    <ButtonGroup>
      <Button
        onClick={onClickCancel}
        variant="outline"
        colorScheme="gray"
        isDisabled={loadingSubmit}
      >
        Cancel
      </Button>

      <Button
        isLoading={loadingSubmit}
        onClick={() =>
          !!destinationFolderId && onClickSubmitMove(destinationFolderId)
        }
        isDisabled={!canShowFolders}
      >
        {submitButtonText}
      </Button>
    </ButtonGroup>
  );

  const content = canShowFolders ? (
    <FolderNavigationList
      disableFolderId={props.disableFolderId}
      currentFolder={currentFolder as Folder}
      onClickNavigateToFolder={onClickNavigateToFolderHandler}
      loadingFolders={loadingFolders}
      subFolders={subFolders}
      selectedFolderId={selectedFolderId}
      onSelectHandler={onSelectHandler}
    />
  ) : null;

  return (
    <>
      <ModalHeader>{header}</ModalHeader>
      <ModalBody p="0">{content}</ModalBody>
      <ModalFooter>{footer}</ModalFooter>
    </>
  );
};

type IMoveItemsModalProps = {
  open: boolean;
  onClose: () => void;
} & IModalContentProps;

export const MoveItemsModal: React.FC<IMoveItemsModalProps> = (props) => {
  return (
    <Modal
      isOpen={props.open}
      onClose={props.onClose}
      isCentered
      size="xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ChakraModalContent>
        <ModalContent {...props} />
      </ChakraModalContent>
    </Modal>
  );
};
